


import React, { useState, useEffect, useRef } from "react";
import "./Leadership.css";
import { aboutUsService } from "../../../../Service/aboutUsService";
import { useLoader } from "../../../../context/LoaderProvider";

const dummyImage =
  "https://images.unsplash.com/photo-1544723795-3fb6469f5b39";

const Leadership = () => {
  const [teamData, setTeamData] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [charLimit, setCharLimit] = useState(100);
  const scrollContainerRef = useRef(null);
  const autoScrollInterval = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    description: "",
    image: null,
    existingImage: "",
    imagePreview: null
  });

  const fetchTeam = async () => {
    try {
      showLoader();
      const response = await aboutUsService.getTeamMembers();
      const result = response.data || response;
      setTeamData(result);
    } catch (error) {
      console.error("Fetch team error:", error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const savedLimit = localStorage.getItem('teamCharLimit');
    if (savedLimit) {
      setCharLimit(parseInt(savedLimit));
    }
    fetchTeam();
  }, []);

  // Auto-scroll functionality
  const startAutoScroll = () => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
    }
    
    autoScrollInterval.current = setInterval(() => {
      if (scrollContainerRef.current && isAutoScrolling && teamData.length > 0) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        
        // If reached the end, scroll back to start
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollContainerRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          // Scroll by 1 card width (280px + gap)
          scrollContainerRef.current.scrollBy({
            left: 300,
            behavior: 'smooth'
          });
        }
      }
    }, 3000); // Scroll every 3 seconds
  };

  const stopAutoScroll = () => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  };

  // Handle hover - pause auto-scroll
  const handleMouseEnter = () => {
    setIsAutoScrolling(false);
    stopAutoScroll();
  };

  const handleMouseLeave = () => {
    setIsAutoScrolling(true);
    startAutoScroll();
  };

  useEffect(() => {
    if (teamData.length > 0) {
      startAutoScroll();
    }
    
    return () => {
      stopAutoScroll();
    };
  }, [teamData]);

  const handleOpen = () => setOpen(true);
  
  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setFormData({
      name: "",
      designation: "",
      description: "",
      image: null,
      existingImage: "",
      imagePreview: null
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert("Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }
      
      const preview = URL.createObjectURL(file);
      setFormData({
        ...formData,
        image: file,
        imagePreview: preview
      });
    }
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      image: null,
      imagePreview: null
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.designation.trim()) {
      alert("Please fill in name and designation");
      return;
    }

    if (formData.description && formData.description.length > charLimit) {
      alert(`Description exceeds ${charLimit} characters limit`);
      return;
    }

    try {
      showLoader();
      if (editId) {
        await aboutUsService.updateMember(
          editId,
          formData.name,
          formData.designation,
          formData.description,
          formData.image,
          formData.existingImage
        );
      } else {
        await aboutUsService.addMember(
          formData.name,
          formData.designation,
          formData.description,
          formData.image
        );
      }
      await fetchTeam();
      handleClose();
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save member. Please try again.");
    } finally {
      hideLoader();
    }
  };

  const handleEdit = (member) => {
    setEditId(member.teamId);
    setFormData({
      name: member.name,
      designation: member.designation,
      description: member.description || "",
      image: null,
      existingImage: member.image,
      imagePreview: member.image
    });
    setOpen(true);
  };

  const handleDelete = async (teamId) => {
    if (window.confirm("Are you sure you want to delete this team member?")) {
      try {
        showLoader();
        await aboutUsService.deleteMember(teamId);
        await fetchTeam();
      } catch (error) {
        console.error("Delete error:", error);
        alert("Failed to delete member. Please try again.");
      } finally {
        hideLoader();
      }
    }
  };

  const handleCharLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setCharLimit(newLimit);
    localStorage.setItem('teamCharLimit', newLimit);
  };

  const remainingChars = charLimit - (formData.description?.length || 0);
  const isNearLimit = remainingChars <= 30;
  const isOverLimit = (formData.description?.length || 0) > charLimit;

  return (
    <section className="team-section">
      <div className="team-container">
        <div className="team-header">
          <div className="team-header-content">
            <h2 className="team-title">
              Meet Our <span>Team</span>
            </h2>
          </div>
          <button className="team-add-button" onClick={handleOpen}>
            <svg className="team-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Member
          </button>
        </div>

        {teamData.length > 0 ? (
          <div 
            className="team-scroll-wrapper"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div 
              className="team-cards-scroll" 
              ref={scrollContainerRef}
            >
              {teamData.map((member) => (
                <div className="team-card" key={member.teamId}>
                  <div className="team-card-actions">
                    <button className="team-action-btn team-edit-btn" onClick={() => handleEdit(member)}>
                      <svg className="team-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                    <button className="team-action-btn team-delete-btn" onClick={() => handleDelete(member.teamId)}>
                      <svg className="team-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                  <img 
                    src={member.image || dummyImage} 
                    alt={member.name} 
                    className="team-card-image" 
                    onError={(e) => e.target.src = dummyImage} 
                  />
                  <div className="team-card-overlay">
                    <div className="team-card-info">
                      <h3 className="team-member-name">{member.name}</h3>
                      <p className="team-member-designation">{member.designation}</p>
                      {member.description && (
                        <p className="team-member-description">{member.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="team-empty-state">
            <div className="team-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h3 className="team-empty-title">No Team Members Yet</h3>
            <button className="team-add-button team-empty-button" onClick={handleOpen}>
              <svg className="team-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add Your First Member
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {open && (
        <div className="team-modal-overlay" onClick={handleClose}>
          <div className="team-modal" onClick={(e) => e.stopPropagation()}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">{editId ? "Edit Team Member" : "Add Team Member"}</h3>
              <button className="team-modal-close" onClick={handleClose}>×</button>
            </div>
            
            <div className="team-modal-body">
              <div className="team-modal-field">
                <label className="team-modal-label">Name *</label>
                <input 
                  type="text" 
                  name="name" 
                  className="team-modal-input" 
                  value={formData.name} 
                  onChange={handleChange} 
                  placeholder="Enter full name"
                />
              </div>

              <div className="team-modal-field">
                <label className="team-modal-label">Designation *</label>
                <input 
                  type="text" 
                  name="designation" 
                  className="team-modal-input" 
                  value={formData.designation} 
                  onChange={handleChange} 
                  placeholder="Enter designation"
                />
              </div>

              <div className="team-modal-field">
                <label className="team-modal-label">Description</label>
                <textarea 
                  name="description" 
                  className={`team-modal-textarea ${isOverLimit ? 'team-modal-textarea-error' : ''}`}
                  value={formData.description} 
                  onChange={handleChange} 
                  rows="4" 
                  placeholder="Brief description about the team member..."
                />
                <div className={`team-modal-char-counter ${isNearLimit ? 'near-limit' : ''} ${isOverLimit ? 'over-limit' : ''}`}>
                  <span className="team-modal-char-count">
                    {formData.description?.length || 0} / {charLimit} characters
                  </span>
                </div>
              </div>

              <div className="team-modal-field">
                <label className="team-modal-label">Profile Image</label>
                <div className="team-modal-image-container">
                  {formData.imagePreview ? (
                    <div className="team-modal-image-preview-wrapper">
                      <img src={formData.imagePreview} alt="Preview" className="team-modal-image-preview" />
                      <button className="team-modal-image-remove" onClick={removeImage} type="button">Remove</button>
                    </div>
                  ) : (
                    <div className="team-modal-image-placeholder">
                      <svg className="team-placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>No image selected</span>
                    </div>
                  )}
                  <button className="team-modal-upload-btn">
                    <svg className="team-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {formData.imagePreview ? 'Change Image' : 'Upload Image'}
                    <input type="file" accept="image/*" onChange={handleImage} className="team-file-input" />
                  </button>
                </div>
              </div>
            </div>

            <div className="team-modal-footer">
              <button className="team-modal-cancel-btn" onClick={handleClose}>Cancel</button>
              <button className="team-modal-save-btn" onClick={handleSave} disabled={isOverLimit || !formData.name.trim() || !formData.designation.trim()}>
                {editId ? "Save" : "Add Member"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Leadership;